import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import {
  GraduationCap, Bell, CheckCircle, XCircle, Clock,
  Mail, Phone, MapPin, FileText, Award, BookOpen,
  Video, Camera, DollarSign, ChevronDown, ChevronUp,
  ShieldCheck, ShieldX, MessageSquare, Users, User,
  Upload, ExternalLink, ThumbsUp, ThumbsDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { SupportSidebar } from "@/components/common/SupportSidebar";
import { cn } from "@/lib/utils";

interface TeacherSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  phoneVerified: boolean;
  avatar: string;
  location: string;
  subjects: string[];
  grades: string[];
  bio: string;
  teachingApproach: string;
  submittedAt: string;
  status: "pending" | "approved" | "rejected";
  reviewComment?: string;
  education: { degree: string; institution: string; fromYear: string; toYear: string }[];
  certifications: { name: string; issuer: string; year: string; uploaded: boolean }[];
  hourlyRate: number;
  hasPhoto: boolean;
  hasVideo: boolean;
  videoUrl?: string;
  experience: string;
}

const mockSubmissions: TeacherSubmission[] = [
  {
    id: "t1",
    name: "Dr. Sarah Mitchell",
    email: "sarah.mitchell@email.com",
    phone: "+1 (555) 234-5678",
    phoneVerified: true,
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200",
    location: "New York, NY",
    subjects: ["Mathematics", "Physics", "Calculus"],
    grades: ["High School", "College"],
    bio: "Passionate mathematics educator with over 10 years of experience helping students master complex concepts. I specialize in making abstract mathematical ideas tangible and accessible. My approach combines visual learning techniques with real-world applications to ensure deep understanding rather than rote memorization. I've helped hundreds of students improve their grades and develop a genuine love for mathematics. Throughout my career, I have developed innovative curriculum materials that have been adopted by multiple schools. My students consistently score in the top percentile on standardized tests. I believe that every student can succeed in mathematics with the right guidance, patience, and encouragement. I am committed to creating an inclusive and supportive learning environment where students feel comfortable asking questions and making mistakes. My teaching philosophy centers on building strong foundational understanding before moving to advanced topics. I regularly attend professional development workshops and conferences to stay current with the latest educational research and teaching methodologies.",
    teachingApproach: "I combine visual learning with real-world applications. Each lesson is tailored to the student's learning style.",
    submittedAt: "2024-01-20",
    status: "pending",
    education: [
      { degree: "Ph.D. Mathematics", institution: "MIT", fromYear: "2010", toYear: "2014" },
      { degree: "M.Sc. Applied Mathematics", institution: "Stanford University", fromYear: "2008", toYear: "2010" },
    ],
    certifications: [
      { name: "State Teaching License", issuer: "NY Department of Education", year: "2015", uploaded: true },
      { name: "Advanced Math Certification", issuer: "NCTM", year: "2018", uploaded: true },
    ],
    hourlyRate: 65,
    hasPhoto: true,
    hasVideo: true,
    videoUrl: "https://youtube.com/watch?v=example1",
    experience: "10+ years",
  },
  {
    id: "t2",
    name: "James Rodriguez",
    email: "james.r@email.com",
    phone: "+1 (555) 876-5432",
    phoneVerified: false,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200",
    location: "Los Angeles, CA",
    subjects: ["English", "Creative Writing"],
    grades: ["Middle School", "High School"],
    bio: "English language arts teacher with a focus on creative writing and critical thinking skills.",
    teachingApproach: "Student-centered approach with emphasis on peer review and workshop-style classes.",
    submittedAt: "2024-01-19",
    status: "pending",
    education: [
      { degree: "M.A. English Literature", institution: "UCLA", fromYear: "2012", toYear: "2014" },
    ],
    certifications: [
      { name: "TESOL Certification", issuer: "Cambridge", year: "2016", uploaded: false },
    ],
    hourlyRate: 45,
    hasPhoto: true,
    hasVideo: false,
    experience: "5 years",
  },
  {
    id: "t3",
    name: "Priya Sharma",
    email: "priya.sharma@email.com",
    phone: "+1 (555) 111-2233",
    phoneVerified: true,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200",
    location: "Chicago, IL",
    subjects: ["Chemistry", "Biology"],
    grades: ["High School", "AP Level"],
    bio: "Award-winning science educator dedicated to hands-on learning. I believe every student can excel in science when given the right support and encouragement. My lab-based teaching approach has consistently produced top results in AP examinations. With over 8 years of experience, I have developed a comprehensive curriculum that integrates theoretical knowledge with practical experiments. I use technology-enhanced learning tools to make complex scientific concepts more accessible and engaging. My students have won numerous science fair competitions at the state and national levels. I am passionate about fostering scientific curiosity and critical thinking skills that will serve students well beyond the classroom. I regularly collaborate with other educators to share best practices and develop innovative teaching strategies.",
    teachingApproach: "Lab-based, hands-on approach with emphasis on real experiments and data analysis.",
    submittedAt: "2024-01-15",
    status: "approved",
    reviewComment: "Excellent credentials and thorough profile. All documents verified.",
    education: [
      { degree: "Ph.D. Biochemistry", institution: "University of Chicago", fromYear: "2009", toYear: "2013" },
    ],
    certifications: [
      { name: "AP Chemistry Certified", issuer: "College Board", year: "2014", uploaded: true },
    ],
    hourlyRate: 70,
    hasPhoto: true,
    hasVideo: true,
    videoUrl: "https://youtube.com/watch?v=example3",
    experience: "8 years",
  },
  {
    id: "t4",
    name: "Michael Chen",
    email: "m.chen@email.com",
    phone: "+1 (555) 444-5566",
    phoneVerified: false,
    avatar: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=200",
    location: "San Francisco, CA",
    subjects: ["Computer Science"],
    grades: ["High School", "College"],
    bio: "Software engineer turned educator.",
    teachingApproach: "",
    submittedAt: "2024-01-18",
    status: "rejected",
    reviewComment: "Bio is too short. Missing certifications. Please resubmit with complete profile information.",
    education: [
      { degree: "B.S. Computer Science", institution: "UC Berkeley", fromYear: "2014", toYear: "2018" },
    ],
    certifications: [],
    hourlyRate: 55,
    hasPhoto: false,
    hasVideo: false,
    experience: "2 years",
  },
];

type ReviewStep = "about" | "description" | "education" | "certification" | "photo" | "video";
type StepVerdict = "pass" | "fail" | null;

const reviewSteps: { id: ReviewStep; title: string; icon: React.ElementType }[] = [
  { id: "about", title: "About", icon: User },
  { id: "description", title: "Description", icon: FileText },
  { id: "education", title: "Education", icon: BookOpen },
  { id: "certification", title: "Certification", icon: Award },
  { id: "photo", title: "Photo", icon: Camera },
  { id: "video", title: "Video", icon: Video },
];

function TeacherReviewCard({
  teacher,
  onAction,
}: {
  teacher: TeacherSubmission;
  onAction: (id: string, action: "approved" | "rejected", comment: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeStep, setActiveStep] = useState<ReviewStep>("about");
  const [stepVerdicts, setStepVerdicts] = useState<Record<ReviewStep, StepVerdict>>({
    about: null, description: null, education: null, certification: null, photo: null, video: null,
  });
  const [comment, setComment] = useState(teacher.reviewComment || "");

  const statusConfig = {
    pending: { label: "Pending", icon: Clock, variant: "warning" as const },
    approved: { label: "Approved", icon: CheckCircle, variant: "success" as const },
    rejected: { label: "Rejected", icon: XCircle, variant: "destructive" as const },
  };
  const config = statusConfig[teacher.status];
  const StatusIcon = config.icon;

  const toggleVerdict = (step: ReviewStep, verdict: "pass" | "fail") => {
    setStepVerdicts(prev => ({ ...prev, [step]: prev[step] === verdict ? null : verdict }));
  };

  const allReviewed = Object.values(stepVerdicts).every(v => v !== null);
  const hasFails = Object.values(stepVerdicts).some(v => v === "fail");
  const wordCount = teacher.bio.split(/\s+/).filter(Boolean).length;

  const handleSubmitReview = () => {
    const action = hasFails ? "rejected" : "approved";
    onAction(teacher.id, action, comment);
  };

  return (
    <Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
      {/* Collapsed header */}
      <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <img src={teacher.avatar} alt={teacher.name} className="w-12 h-12 rounded-xl object-cover ring-2 ring-border" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-foreground text-sm">{teacher.name}</h3>
            <Badge variant={config.variant} className="text-xs">
              <StatusIcon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{teacher.email}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{teacher.location}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden md:block">Submitted {teacher.submittedAt}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded review */}
      {expanded && (
        <div className="border-t border-border">
          {/* Step tabs */}
          <div className="flex border-b border-border overflow-x-auto">
            {reviewSteps.map((step) => {
              const Icon = step.icon;
              const verdict = stepVerdicts[step.id];
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-xs font-medium border-b-2 transition-all whitespace-nowrap",
                    activeStep === step.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {step.title}
                  {verdict === "pass" && <CheckCircle className="w-3.5 h-3.5 text-success" />}
                  {verdict === "fail" && <XCircle className="w-3.5 h-3.5 text-destructive" />}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {/* Step content */}
            <div className="mb-4">
              {/* About */}
              {activeStep === "about" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoField label="Full Name" value={teacher.name} />
                    <InfoField label="Email" value={teacher.email} icon={<Mail className="w-3.5 h-3.5" />} />
                    <InfoField
                      label="Phone"
                      value={teacher.phone}
                      icon={<Phone className="w-3.5 h-3.5" />}
                      badge={teacher.phoneVerified
                        ? <Badge variant="outline" className="text-xs gap-1 border-success/30 text-success bg-success/5"><ShieldCheck className="w-3 h-3" />Verified</Badge>
                        : <Badge variant="outline" className="text-xs gap-1 border-destructive/30 text-destructive bg-destructive/5"><ShieldX className="w-3 h-3" />Not verified</Badge>
                      }
                    />
                    <InfoField label="Location" value={teacher.location} icon={<MapPin className="w-3.5 h-3.5" />} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Subjects</p>
                    <div className="flex flex-wrap gap-1.5">
                      {teacher.subjects.map(s => <Badge key={s} variant="subject" className="text-xs">{s}</Badge>)}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Grade Levels</p>
                    <div className="flex flex-wrap gap-1.5">
                      {teacher.grades.map(g => <Badge key={g} variant="outline" className="text-xs">{g}</Badge>)}
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              {activeStep === "description" && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Professional Bio</p>
                      <Badge variant={wordCount >= 200 ? "success" : "warning"} className="text-xs">
                        {wordCount} words {wordCount >= 200 ? "✓" : "(min 200)"}
                      </Badge>
                    </div>
                    <div className="bg-muted/30 rounded-xl p-4 text-sm text-foreground/80 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: teacher.bio }} />
                  </div>
                  {teacher.teachingApproach && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Teaching Approach</p>
                      <p className="bg-muted/30 rounded-xl p-4 text-sm text-foreground/80 leading-relaxed">{teacher.teachingApproach}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Education */}
              {activeStep === "education" && (
                <div className="space-y-3">
                  {teacher.education.length === 0 ? (
                    <p className="text-sm text-destructive/80 bg-destructive/5 rounded-lg p-4">No education history provided</p>
                  ) : (
                    teacher.education.map((edu, i) => (
                      <div key={i} className="flex items-center gap-3 bg-muted/30 rounded-xl p-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <GraduationCap className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{edu.degree}</p>
                          <p className="text-xs text-muted-foreground">{edu.institution} • {edu.fromYear}–{edu.toYear}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Certification */}
              {activeStep === "certification" && (
                <div className="space-y-3">
                  {teacher.certifications.length === 0 ? (
                    <p className="text-sm text-destructive/80 bg-destructive/5 rounded-lg p-4">No certifications uploaded</p>
                  ) : (
                    teacher.certifications.map((cert, i) => (
                      <div key={i} className="overflow-hidden rounded-xl border border-border bg-gradient-to-br from-card to-accent/20">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border/50">
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-primary" />
                            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Certificate #{i + 1}</span>
                          </div>
                          <Badge variant={cert.uploaded ? "success" : "warning"} className="text-xs gap-1">
                            {cert.uploaded ? <><CheckCircle className="w-3 h-3" /> Document attached</> : <><Upload className="w-3 h-3" /> Missing</>}
                          </Badge>
                        </div>
                        <div className="p-4">
                          <p className="text-sm font-medium text-foreground">{cert.name}</p>
                          <p className="text-xs text-muted-foreground">{cert.issuer} • {cert.year}</p>
                          {cert.uploaded && (
                            <button className="mt-2 text-xs text-primary flex items-center gap-1 hover:underline">
                              <ExternalLink className="w-3 h-3" /> View document
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Photo */}
              {activeStep === "photo" && (
                <div className="space-y-4">
                  {teacher.hasPhoto ? (
                    <div className="flex items-start gap-4">
                      <img src={teacher.avatar} alt={teacher.name} className="w-32 h-32 rounded-2xl object-cover ring-2 ring-border" />
                      <div className="space-y-2">
                        <Badge variant="success" className="text-xs gap-1"><CheckCircle className="w-3 h-3" /> Photo uploaded</Badge>
                        <p className="text-xs text-muted-foreground">Professional headshot provided</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-destructive/5 rounded-xl p-6 text-center">
                      <Camera className="w-8 h-8 text-destructive/50 mx-auto mb-2" />
                      <p className="text-sm text-destructive/80">No profile photo uploaded</p>
                    </div>
                  )}
                </div>
              )}

              {/* Video */}
              {activeStep === "video" && (
                <div className="space-y-4">
                  {teacher.hasVideo ? (
                    <div className="space-y-3">
                      <Badge variant="success" className="text-xs gap-1"><CheckCircle className="w-3 h-3" /> Video provided</Badge>
                      {teacher.videoUrl && (
                        <div className="bg-muted/30 rounded-xl p-4">
                          <p className="text-xs text-muted-foreground mb-1">Video URL</p>
                          <a href={teacher.videoUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                            {teacher.videoUrl} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-destructive/5 rounded-xl p-6 text-center">
                      <Video className="w-8 h-8 text-destructive/50 mx-auto mb-2" />
                      <p className="text-sm text-destructive/80">No introduction video uploaded</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pass/Fail buttons for current step */}
            {teacher.status === "pending" && (
              <div className="flex items-center gap-2 mb-4 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground mr-2">Mark this section:</span>
                <Button
                  size="sm"
                  variant={stepVerdicts[activeStep] === "pass" ? "default" : "outline"}
                  className={cn("gap-1.5 h-8 text-xs", stepVerdicts[activeStep] === "pass" && "bg-success hover:bg-success/90 text-success-foreground")}
                  onClick={() => toggleVerdict(activeStep, "pass")}
                >
                  <ThumbsUp className="w-3.5 h-3.5" /> Pass
                </Button>
                <Button
                  size="sm"
                  variant={stepVerdicts[activeStep] === "fail" ? "destructive" : "outline"}
                  className="gap-1.5 h-8 text-xs"
                  onClick={() => toggleVerdict(activeStep, "fail")}
                >
                  <ThumbsDown className="w-3.5 h-3.5" /> Fail
                </Button>
              </div>
            )}

            {/* Final submit area */}
            {teacher.status === "pending" && (
              <div className="border-t border-border pt-4 space-y-3">
                {/* Summary of verdicts */}
                <div className="flex flex-wrap gap-2">
                  {reviewSteps.map(step => {
                    const v = stepVerdicts[step.id];
                    return (
                      <div key={step.id} className={cn(
                        "flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border",
                        v === "pass" ? "border-success/30 bg-success/5 text-success" :
                        v === "fail" ? "border-destructive/30 bg-destructive/5 text-destructive" :
                        "border-border bg-muted/30 text-muted-foreground"
                      )}>
                        {v === "pass" ? <CheckCircle className="w-3 h-3" /> : v === "fail" ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {step.title}
                      </div>
                    );
                  })}
                </div>

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3" /> Review Comment
                  </p>
                  <Textarea
                    placeholder="Add a comment for this review (required for rejection)..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="text-sm min-h-[80px]"
                  />
                </div>

                <Button
                  className={cn(
                    "w-full gap-2",
                    allReviewed && !hasFails ? "bg-success hover:bg-success/90 text-success-foreground" :
                    allReviewed && hasFails ? "" : ""
                  )}
                  variant={allReviewed && hasFails ? "destructive" : "default"}
                  disabled={!allReviewed || (hasFails && !comment.trim())}
                  onClick={handleSubmitReview}
                >
                  {!allReviewed ? (
                    <>Review all sections to submit</>
                  ) : hasFails ? (
                    <><XCircle className="w-4 h-4" /> Submit as Rejected</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" /> Submit as Approved</>
                  )}
                </Button>
              </div>
            )}

            {/* Already reviewed */}
            {teacher.status !== "pending" && (
              <div className={cn("rounded-xl p-4 text-sm", teacher.status === "approved" ? "bg-success/5 text-success" : "bg-destructive/5 text-destructive")}>
                <p className="font-medium flex items-center gap-1.5">
                  {teacher.status === "approved" ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {teacher.status === "approved" ? "Profile Approved" : "Profile Rejected"}
                </p>
                {teacher.reviewComment && <p className="text-xs mt-1 opacity-80">{teacher.reviewComment}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function InfoField({ label, value, icon, badge }: { label: string; value: string; icon?: React.ReactNode; badge?: React.ReactNode }) {
  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="text-sm text-foreground">{value}</span>
        {badge}
      </div>
    </div>
  );
}

export default function SupportDashboard() {
  usePageTitle('Support | BookMyCoaching');
  const [submissions, setSubmissions] = useState(mockSubmissions);

  const counts = {
    pending: submissions.filter(s => s.status === "pending").length,
    approved: submissions.filter(s => s.status === "approved").length,
    rejected: submissions.filter(s => s.status === "rejected").length,
  };

  const handleAction = (id: string, action: "approved" | "rejected", comment: string) => {
    setSubmissions(prev =>
      prev.map(s => s.id === id ? { ...s, status: action, reviewComment: comment } : s)
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <SupportSidebar />

          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
              <div className="flex items-center justify-between px-4 md:px-6 py-3">
                <div className="flex items-center gap-4">
                  <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
                  <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                      <GraduationCap className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <span className="font-display font-semibold text-lg text-foreground hidden sm:block">BookMyCoaching</span>
                  </Link>
                  <div className="hidden sm:block w-px h-6 bg-border" />
                  <div className="hidden sm:block">
                    <h1 className="font-medium text-sm text-foreground">Profile Reviews</h1>
                    <p className="text-xs text-muted-foreground">Review and approve teacher profiles</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="w-5 h-5 text-muted-foreground" />
                    {counts.pending > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-warning text-warning-foreground text-xs rounded-full flex items-center justify-center font-bold">
                        {counts.pending}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </header>

            {/* Content */}
            <main className="flex-1 p-4 md:p-6 overflow-auto">
              <Tabs defaultValue="pending" className="space-y-4">
                <TabsList className="w-full sm:w-auto">
                  <TabsTrigger value="pending" className="gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Pending
                    {counts.pending > 0 && <Badge variant="warning" className="h-5 min-w-5 px-1.5 text-xs ml-1">{counts.pending}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Approved
                    <Badge variant="outline" className="h-5 min-w-5 px-1.5 text-xs ml-1">{counts.approved}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="gap-1.5">
                    <XCircle className="w-3.5 h-3.5" /> Rejected
                    <Badge variant="outline" className="h-5 min-w-5 px-1.5 text-xs ml-1">{counts.rejected}</Badge>
                  </TabsTrigger>
                </TabsList>

                {(["pending", "approved", "rejected"] as const).map(status => (
                  <TabsContent key={status} value={status} className="space-y-3">
                    {submissions.filter(s => s.status === status).length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No {status} profiles.</p>
                      </div>
                    ) : (
                      submissions.filter(s => s.status === status).map(teacher => (
                        <TeacherReviewCard key={teacher.id} teacher={teacher} onAction={handleAction} />
                      ))
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
